/*
  Zain Art V3 - Enterprise Theme Core Orchestrator (theme.js)
  Integrates events, storage, observers, page routers, and component registries.
*/

window.ZainArt = {
  version: "3.0.0",
  
  // 1. Theme Context Layer
  Context: window.ZainArtContext || {
    template: 'index',
    page_type: 'index',
    locale: 'en',
    currency: 'QAR',
    customer_logged_in: false
  },

  // 2. Services Registry
  Services: {},

  // 3. Logger Service
  Logger: {
    level: 'info', // 'debug' | 'info' | 'warn' | 'error'
    debug(msg, data = '') { if (this.level === 'debug') console.debug('[ZainArt DEBUG]', msg, data); },
    info(msg, data = '') { console.info('[ZainArt INFO]', msg, data); },
    warn(msg, data = '') { console.warn('[ZainArt WARN]', msg, data); },
    error(msg, data = '') { console.error('[ZainArt ERROR]', msg, data); }
  },

  // 4. Component Registries
  Components: {
    registry: {},
    register(metadata, compClass) {
      this.registry[metadata.name] = { class: compClass, metadata };
      ZainArt.Logger.debug(`Registered component: ${metadata.name} v${metadata.version}`);
    },
    init() {
      ZainArt.Logger.debug('Initializing component registry...');
      Object.keys(this.registry).forEach(name => {
        const item = this.registry[name];
        if (item.metadata.autoload) {
          const elements = document.querySelectorAll(item.metadata.selector);
          elements.forEach(el => {
            try {
              new item.class(el);
            } catch (e) {
              ZainArt.Logger.error(`Failed to load component "${name}":`, e);
            }
          });
        }
      });
    }
  },

  // 5. Page Controller Routing
  Pages: {
    init() {
      const pageType = ZainArt.Context.page_type;
      ZainArt.Logger.info(`Page Router executing for pageType: ${pageType}`);
      
      if (this[pageType]) {
        try {
          this[pageType]();
        } catch (e) {
          ZainArt.Logger.error(`Error running Page Handler for ${pageType}`, e);
        }
      }
    },
    index() {
      ZainArt.Logger.info('Homepage controller active');
      // Initialize homepage specific modules here
    },
    product() {
      ZainArt.Logger.info('Product details page controller active');
    },
    builder() {
      ZainArt.Logger.info('Builder configuration controller active');
    }
  }
};

// 6. Universal Slider/Carousel Component
class ZainArtCarousel {
  constructor(element) {
    this.element = element;
    this.track = element.querySelector('.hp-carousel-track');
    this.autoplay = element.dataset.autoplay === 'true';
    this.speed = parseInt(element.dataset.speed || 4) * 1000;
    this.init();
  }

  init() {
    if (this.autoplay) {
      this.startAutoplay();
    }
  }

  startAutoplay() {
    setInterval(() => {
      const slide = this.element.querySelector('.hp-carousel-slide');
      if (!slide) return;
      const slideWidth = slide.offsetWidth + 15;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      if (this.track.scrollLeft >= maxScroll - 5) {
        this.track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        this.track.scrollBy({ left: slideWidth, behavior: 'smooth' });
      }
    }, this.speed);
  }
}

// Autowire bootstrap sequences on DOM load
document.addEventListener('DOMContentLoaded', () => {
  ZainArt.Logger.info('Bootstrapping ZainArt Framework V3...');
  
  // Initialize Utilities on global namespace
  ZainArt.Events = new window.ZainArtEventBusClass();
  ZainArt.Storage = new window.ZainArtStorageClass();
  ZainArt.Observer = new window.ZainArtObserverClass();
  ZainArt.Animation = new window.ZainArtAnimationClass();

  // Register components in registry
  ZainArt.Components.register({
    name: 'carousel',
    version: '1.0',
    selector: '.js-carousel-container',
    autoload: true
  }, ZainArtCarousel);

  // Run Registries and Page Routers
  ZainArt.Components.init();
  ZainArt.Pages.init();

  ZainArt.Events.emit('theme:ready');
  ZainArt.Logger.info('ZainArt Framework V3 ready.');
  
  // Initialize Global Wishlist
  if (window.ZainArtWishlist) {
    window.ZainArtWishlist.init();
  }
});

// Zain Art Wishlist State Manager
window.ZainArtWishlist = {
  get() {
    try {
      return JSON.parse(localStorage.getItem('hp_wishlist') || '[]');
    } catch(e) {
      return [];
    }
  },

  set(list) {
    try {
      localStorage.setItem('hp_wishlist', JSON.stringify(list));
    } catch(e) {}
  },

  has(handle) {
    return this.get().includes(handle);
  },

  toggle(handle) {
    let list = this.get();
    if (list.includes(handle)) {
      list = list.filter(h => h !== handle);
    } else {
      list.unshift(handle);
      if (list.length > 50) list.pop(); // Max 50 items
    }
    this.set(list);
    this.updateUI();

    // Trigger tab reload if we are on the account page
    const wishlistGrid = document.getElementById('hp-wishlist-grid');
    if (wishlistGrid && window.ZainArtAccountLoadWishlist) {
      window.ZainArtAccountLoadWishlist();
    }
  },

  updateUI() {
    this.updateBadges();
    this.updateButtons();
  },

  updateBadges() {
    const count = this.get().length;
    document.querySelectorAll('.wishlist-count').forEach(badge => {
      badge.textContent = count;
      badge.style.display = count > 0 ? 'inline-flex' : 'none';
    });
  },

  updateButtons() {
    const list = this.get();
    
    // Style PDP and card buttons across all sections
    document.querySelectorAll('.hp-wishlist-toggle-btn, .hp-wishlist-circle-btn, [data-wishlist-toggle], .js-wishlist-btn').forEach(btn => {
      let handle = btn.getAttribute('data-handle') || btn.getAttribute('data-product-handle');
      if (!handle) {
        const card = btn.closest('.hp-flower-product-card, .hp-grid-item, .card, .product-card, .ht-prod-card');
        const prodLink = card ? card.querySelector('a[href*="/products/"]') : null;
        if (prodLink) {
          const href = prodLink.getAttribute('href');
          if (href && href.includes('/products/')) {
            handle = href.split('/products/')[1].split('?')[0].split('#')[0];
            btn.setAttribute('data-handle', handle);
          }
        }
      }
      if (!handle) return;

      const isActive = list.includes(handle);
      
      if (isActive) {
        btn.classList.add('active');
        const svgPath = btn.querySelector('svg path, svg polygon');
        if (svgPath) {
          svgPath.setAttribute('fill', '#e07b85');
          svgPath.setAttribute('stroke', '#e07b85');
        }
        btn.style.color = '#e07b85';

        // Custom styling for PDP detail page button
        if (btn.classList.contains('hp-pdp-btn-wishlist')) {
          const btnText = btn.querySelector('.wishlist-btn-text');
          if (btnText) btnText.textContent = 'In Wishlist';
          btn.style.background = '#fff0f3';
          btn.style.borderColor = '#e07b85';
        }
      } else {
        btn.classList.remove('active');
        const svgPath = btn.querySelector('svg path, svg polygon');
        if (svgPath) {
          svgPath.setAttribute('fill', 'none');
          svgPath.setAttribute('stroke', 'currentColor');
        }
        btn.style.color = '#666666';

        // Custom styling for PDP detail page button
        if (btn.classList.contains('hp-pdp-btn-wishlist')) {
          const btnText = btn.querySelector('.wishlist-btn-text');
          if (btnText) btnText.textContent = 'Add to Wishlist';
          btn.style.background = '#faf8f5';
          btn.style.borderColor = '#eae3d7';
        }
      }
    });
  },

  init() {
    this.updateUI();

    // Universal Event Delegation for Wishlist clicks
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.hp-wishlist-toggle-btn, .hp-wishlist-circle-btn, [data-wishlist-toggle], .js-wishlist-btn');
      if (btn) {
        e.preventDefault();
        e.stopPropagation();
        
        let handle = btn.getAttribute('data-handle') || btn.getAttribute('data-product-handle');
        if (!handle) {
          const card = btn.closest('.hp-flower-product-card, .hp-grid-item, .card, .product-card, .ht-prod-card');
          const prodLink = card ? card.querySelector('a[href*="/products/"]') : null;
          if (prodLink) {
            const href = prodLink.getAttribute('href');
            if (href && href.includes('/products/')) {
              handle = href.split('/products/')[1].split('?')[0].split('#')[0];
              btn.setAttribute('data-handle', handle);
            }
          }
        }

        if (handle) {
          this.toggle(handle);
        } else {
          // Fallback handle for sample items
          this.toggle('sample-wall-art-' + Math.floor(Math.random() * 1000));
        }
      }
    });

    // Universal Event Delegation for Product Card Add to Cart Buttons
    document.addEventListener('click', function(e) {
      const addBtn = e.target.closest('.js-hp-card-add-btn');
      if (addBtn) {
        // If button has already added the product, clicking it opens Cart Drawer or /cart directly!
        if (addBtn.classList.contains('is-added')) {
          if (window.openCartDrawer) {
            window.openCartDrawer();
          } else {
            window.location.href = '/cart';
          }
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        const variantId = addBtn.getAttribute('data-variant-id');
        if (!variantId) return;

        addBtn.disabled = true;
        addBtn.innerHTML = `
          <svg style="animation: spin 1s linear infinite;" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <line x1="12" y1="2" x2="12" y2="6"></line>
            <line x1="12" y1="18" x2="12" y2="22"></line>
            <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
            <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
            <line x1="2" y1="12" x2="6" y2="12"></line>
            <line x1="18" y1="12" x2="22" y2="12"></line>
            <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
            <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
          </svg>
          <span>Adding...</span>
        `;

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(variantId), quantity: 1 }] })
        })
        .then(res => res.json())
        .then(data => {
          // Permanently set button to "View Cart" state!
          const isRtlTheme = document.documentElement.dir === 'rtl' || document.documentElement.lang === 'ar' || window.location.pathname.indexOf('/ar') === 0;
          const viewCartTxt = isRtlTheme ? 'عرض السلة' : 'View Cart';

          addBtn.disabled = false;
          addBtn.classList.add('is-added');
          addBtn.style.background = '#2c261e';
          addBtn.style.borderColor = '#2c261e';
          addBtn.style.color = '#ffffff';
          addBtn.innerHTML = `
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            <span>${viewCartTxt}</span>
          `;
          
          // Sync all buttons & cart counts
          if (window.syncCardButtonsWithCart) window.syncCardButtonsWithCart();

          // Dispatch cart updates for any third-party drawer/listeners
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: data }));

          // Immediately open Cart Drawer or trigger GoKwik!
          if (window.openCartDrawer) {
            window.openCartDrawer();
          } else if (window.gokwikCart && typeof window.gokwikCart.open === 'function') {
            window.gokwikCart.open();
          } else if (window.kwikCart && typeof window.kwikCart.open === 'function') {
            window.kwikCart.open();
          } else {
            document.dispatchEvent(new CustomEvent('gokwik:cart:open', { bubbles: true }));
            showCartToastNotification();
          }
        })
        .catch(err => {
          console.error('Error adding card item to cart:', err);
          const isRtlTheme = document.documentElement.dir === 'rtl' || document.documentElement.lang === 'ar' || window.location.pathname.indexOf('/ar') === 0;
          addBtn.disabled = false;
          addBtn.innerHTML = `<span>${isRtlTheme ? 'أضف إلى السلة' : 'Add to Cart'}</span>`;
        });
      }
    });

    window.syncCardButtonsWithCart = function() {
      fetch('/cart.js')
        .then(r => r.json())
        .then(cart => {
          const isRtlTheme = document.documentElement.dir === 'rtl' || document.documentElement.lang === 'ar' || window.location.pathname.indexOf('/ar') === 0;
          const viewCartTxt = isRtlTheme ? 'عرض السلة' : 'View Cart';
          const cartVariantIds = (cart.items || []).map(i => String(i.variant_id));
          document.querySelectorAll('.js-hp-card-add-btn').forEach(btn => {
            const variantId = btn.getAttribute('data-variant-id');
            if (variantId && cartVariantIds.includes(String(variantId))) {
              btn.classList.add('is-added');
              btn.style.background = '#2c261e';
              btn.style.borderColor = '#2c261e';
              btn.style.color = '#ffffff';
              btn.innerHTML = `
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>${viewCartTxt}</span>
              `;
            }
          });
        })
        .catch(e => console.error('Error syncing card buttons:', e));
    };

    // Run sync on load & cart updates
    window.syncCardButtonsWithCart();
    document.addEventListener('cart:updated', function() {
      window.syncCardButtonsWithCart();
    });

    function showCartToastNotification() {
      let toast = document.getElementById('hp-cart-toast');
      if (!toast) {
        toast = document.createElement('div');
        toast.id = 'hp-cart-toast';
        toast.style.cssText = `
          position: fixed;
          top: 25px;
          right: 25px;
          z-index: 999999;
          background: #012d1d;
          color: #ffffff;
          padding: 14px 22px;
          border-radius: 30px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
          display: flex;
          align-items: center;
          gap: 12px;
          font-family: 'Inter', sans-serif;
          font-size: 0.9rem;
          font-weight: 600;
          border: 1px solid #c5a059;
          transform: translateY(-20px);
          opacity: 0;
          transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
        `;
        document.body.appendChild(toast);
      }

      toast.innerHTML = `
        <span style="display: flex; align-items: center; justify-content: center; width: 22px; height: 22px; background: #c5a059; color: #012d1d; border-radius: 50%; font-size: 12px; font-weight: 900;">✓</span>
        <span>Item added to cart!</span>
        <a href="/cart" style="color: #c5a059; font-weight: 800; text-decoration: underline; margin-left: 6px; font-size: 0.88rem;">View Cart &rarr;</a>
      `;

      requestAnimationFrame(() => {
        toast.style.opacity = '1';
        toast.style.transform = 'translateY(0)';
      });

      if (window.cartToastTimer) clearTimeout(window.cartToastTimer);
      window.cartToastTimer = setTimeout(() => {
        toast.style.opacity = '0';
        toast.style.transform = 'translateY(-20px)';
      }, 4500);
    }
  }
};

